/*
 * The (sub-organization, line-of-business) scope every Clive Configuration tab is organised by.
 *
 * Hoisted here because several tabs derive the same rows the same way — QA Configuration and
 * Agent Configuration today — and they had drifted into two independent copies.
 * The rows come from the sysconfig organization context rather than from a backend endpoint,
 * so there is nothing to fetch and every tab can render its table before any config loads.
 */

import type { OrganizationModel } from '~/components/types/organization-types';

/**
 * A single (sub-organization, line-of-business) scope.
 *
 * `sub_organization_id` is `null` for a flat organization (sub-organizations disabled), whose
 * configurations are scoped at the organization level — the backend persists and resolves
 * those with `sub_organization_id IS NULL`.
 */
export interface ScopeRow {
  sub_organization_id: number | null;
  sub_organization_name: string;
  lob: string;
}

/**
 * One row per (sub-organization, LOB) the organization supports.
 *
 * A flat organization has no SubOrganization rows, so each of its scopes is an org-level LOB
 * with a `null` sub_organization_id and the organization's own name in the column a sub-org
 * name would occupy — which is what lets one table serve both shapes.
 */
export const buildScopeRows = (organization: OrganizationModel): ScopeRow[] => {
  const subOrgs = organization.sub_organizations ?? [];
  if (organization.sub_organizations_enabled) {
    return subOrgs.flatMap((subOrg) =>
      (subOrg.supported_lobs ?? []).map((supportedLob) => ({
        sub_organization_id: subOrg.id,
        sub_organization_name: subOrg.name ?? '',
        lob: supportedLob.lob ?? '',
      }))
    );
  }
  return (organization.supported_lobs ?? []).map((supportedLob) => ({
    sub_organization_id: null,
    sub_organization_name: organization.name ?? '',
    lob: supportedLob.lob ?? '',
  }));
};
